"use client";

import { useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { es } from "@daypicker/react/locale";
import { useLanguage } from "@tillix/i18n/react";
import { Spinner } from "@tillix/ui-atoms/atoms/spinner";
import { Button } from "@tillix/ui-atoms/button";
import { Card } from "@tillix/ui-atoms/card";
import { DatePicker } from "@tillix/ui-atoms/date-picker";
import { Radio, RadioGroup } from "@tillix/ui-atoms/radio-group";
import { toastManager } from "@tillix/ui-atoms/toast";
import { fetchApptDates, rescheduleOrder } from "../actions";
import { useAppointmentT } from "../i18n/use-t";
import { isCompleteContact } from "../lib/contact";
import { dashboardNoticeHref } from "../lib/dashboard-notice";
import {
  applyPickedDays,
  compactToLocalDate,
  localDateToCompact,
  pickerBounds,
} from "../lib/date-picker";
import { errorKey } from "../lib/errors";
import { formatDateLabel, formatTimeLabel } from "../lib/sap-format";
import { sessionExpiredHref } from "../lib/session-expired";
import { useSapFailureFlags } from "../lib/use-sap-failure-flags";
import ContactCapture from "./ContactCapture";
import type { ContactInput } from "../lib/contact";
import type { AppointmentDay } from "../types";

interface SlotPickerProps {
  orderNumber: string;
  functionalLocation: string;
  /** Order capability token from a Nudge-UUID entry; cookie auth otherwise. */
  grant?: string;
  /** The SAP order has no contact info — collect one for the confirmation nudge. */
  needsContact?: boolean;
}

/**
 * The browser's zone makes "today" the customer's today on the server; the
 * action already returns only bookable windows on days after it.
 */
function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const CHIP_CLASS = "shrink-0 rounded-md border px-4 py-2 text-sm";

export default function SlotPicker({
  orderNumber,
  functionalLocation,
  grant,
  needsContact = false,
}: SlotPickerProps) {
  const t = useAppointmentT();
  const { language } = useLanguage();
  const router = useRouter();
  const sapFailures = useSapFailureFlags();
  /** What the first load produced; "show earliest dates" restores it. */
  const [initialDays, setInitialDays] = useState<AppointmentDay[] | null>(null);
  /** The strip on screen: the first load, or the picked day and what follows. */
  const [days, setDays] = useState<AppointmentDay[] | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedWindowId, setSelectedWindowId] = useState<string>("");
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  /** Sequence number of the latest pick; older responses are ignored. */
  const pickSeq = useRef(0);
  const noneMessageId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [contact, setContact] = useState<ContactInput>({
    channel: "email",
    value: "",
  });

  useEffect(() => {
    let active = true;
    fetchApptDates(
      orderNumber,
      functionalLocation,
      browserTimeZone(),
      undefined,
      sapFailures,
    )
      .then((result) => {
        if (!active) return;
        if (result.ok) {
          setInitialDays(result.data);
          setDays(result.data);
          setSelectedDate(result.data[0]?.date ?? "");
        } else {
          setDays([]);
          setLoadError(result.error);
        }
      })
      .catch(() => {
        if (!active) return;
        setDays([]);
        setLoadError("");
      });
    return () => {
      active = false;
    };
  }, [orderNumber, functionalLocation, sapFailures]);

  if (days === null) {
    return (
      <Card>
        <Card.Content className="flex items-center gap-2 py-8 text-muted-foreground">
          <Spinner /> {t("slots.loading")}
        </Card.Content>
      </Card>
    );
  }

  if (days.length === 0) {
    return (
      <Card>
        <Card.Content className="py-8 text-center text-muted-foreground">
          {loadError !== null
            ? t(errorKey("slots.loadErrors", loadError))
            : t("slots.none")}
        </Card.Content>
      </Card>
    );
  }

  // Only rendered after the client-side load, so `new Date()` here cannot
  // disagree with a server render.
  const bounds = initialDays
    ? pickerBounds({ initialDays, today: localDateToCompact(new Date()) })
    : null;
  // The selected chip must carry windows; an unavailable picked day never
  // becomes the active day.
  const activeDay =
    days.find((d) => d.date === selectedDate && d.windows.length > 0) ??
    days.find((d) => d.windows.length > 0);
  const pickedUnavailable =
    pickedDate !== null &&
    days.some((d) => d.date === pickedDate && d.windows.length === 0);

  const handlePick = async (date: Date | undefined) => {
    if (!date) return;
    const picked = localDateToCompact(date);
    const previousPicked = pickedDate;
    pickSeq.current += 1;
    const seq = pickSeq.current;
    setPickedDate(picked);
    setPickLoading(true);
    setPickError(null);
    setSelectedWindowId("");
    try {
      const result = await fetchApptDates(
        orderNumber,
        functionalLocation,
        browserTimeZone(),
        picked,
        sapFailures,
      );
      if (seq !== pickSeq.current) return;
      if (result.ok) {
        const next = applyPickedDays(picked, result.data);
        setDays(next.days);
        setSelectedDate(
          next.pickedUnavailable ? (next.days[1]?.date ?? picked) : picked,
        );
      } else {
        // The previous strip stays and the pick is rolled back, so the
        // trigger, strip and message agree and the same date can be retried.
        setPickedDate(previousPicked);
        setPickError(result.error);
      }
    } catch {
      if (seq !== pickSeq.current) return;
      setPickedDate(previousPicked);
      setPickError("");
    } finally {
      if (seq === pickSeq.current) setPickLoading(false);
    }
  };

  const handleShowEarliest = () => {
    if (!initialDays) return;
    // Drop any pick still in flight.
    pickSeq.current += 1;
    setDays(initialDays);
    setSelectedDate(initialDays[0]?.date ?? "");
    setPickedDate(null);
    setSelectedWindowId("");
    setPickError(null);
    setPickLoading(false);
  };

  const handleConfirm = async () => {
    const slot = activeDay?.windows.find((w) => w.id === selectedWindowId);
    if (!slot) return;
    setSubmitting(true);
    setSubmitError(null);
    const result = await rescheduleOrder(
      orderNumber,
      {
        date: slot.date,
        timeFrom: slot.timeFrom,
        timeTo: slot.timeTo,
      },
      grant,
      needsContact
        ? { channel: contact.channel, value: contact.value.trim() }
        : undefined,
      sapFailures,
    );
    if (result.ok) {
      // Back to the dashboard (public path; the wgl rewrite maps it to
      // /f/appointment), which shows the approved success banner for this
      // order. The dashboard RSC is dynamic (reads the auth cookie), so the
      // navigation fetches fresh data — do not call router.refresh() here,
      // it supersedes the pending push and the navigation never lands.
      // Keep `submitting` true so the button stays disabled while navigating.
      router.push(dashboardNoticeHref("rescheduled", orderNumber));
    } else if (result.error === "NOT_AUTHED") {
      // Session timed out mid-flow: back to the gate with the timeout dialog.
      router.replace(sessionExpiredHref());
    } else {
      setSubmitError(result.error);
      toastManager.add({
        title: t(errorKey("slots.errors", result.error)),
        type: "error",
      });
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t("order.pickTitle")}</Card.Title>
        <Card.Description>
          {t.list("order.pickDescription").map((line, index) => (
            <p key={index.toString()}>{line}</p>
          ))}
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-6">
        {pickLoading ? (
          <div className="flex items-center gap-2 py-4 text-muted-foreground">
            <Spinner /> {t("slots.loading")}
          </div>
        ) : (
          <>
            {/* Date strip */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {days.map((day) =>
                day.windows.length === 0 ? (
                  <button
                    aria-describedby={
                      day.date === pickedDate ? noneMessageId : undefined
                    }
                    className={`${CHIP_CLASS} cursor-not-allowed border-border bg-background text-muted-foreground line-through`}
                    disabled={true}
                    key={day.date}
                    type="button"
                  >
                    {formatDateLabel(day.date, language)}
                  </button>
                ) : (
                  <button
                    className={`${CHIP_CLASS} transition-colors ${
                      day.date === activeDay?.date
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground hover:bg-muted"
                    }`}
                    key={day.date}
                    onClick={() => {
                      setSelectedDate(day.date);
                      setSelectedWindowId("");
                    }}
                    type="button"
                  >
                    {formatDateLabel(day.date, language)}
                  </button>
                ),
              )}
            </div>

            {pickedDate !== null && pickedUnavailable && (
              <p className="text-muted-foreground text-sm" id={noneMessageId}>
                {t(activeDay ? "slots.noneOn" : "slots.noneOnOrAfter", {
                  date: formatDateLabel(pickedDate, language),
                })}
              </p>
            )}

            {/* Slot cards for the active day */}
            {activeDay && (
              <div className="flex flex-col gap-2">
                <p className="font-medium text-sm">
                  {t("slots.availableFor", {
                    date: formatDateLabel(activeDay.date, language),
                  })}
                </p>
                <RadioGroup
                  onValueChange={(value) => setSelectedWindowId(String(value))}
                  value={selectedWindowId}
                >
                  {activeDay.windows.map((slot) => (
                    // biome-ignore lint/a11y/noLabelWithoutControl: Radio is the labeled control; Base UI renders it as an input
                    <label
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-4 py-3 hover:bg-muted"
                      key={slot.id}
                    >
                      <Radio value={slot.id} />
                      <span className="flex flex-col">
                        <span className="font-medium text-sm">
                          {slot.description}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {t("slots.window", {
                            from: formatTimeLabel(slot.timeFrom, language),
                            to: formatTimeLabel(slot.timeTo, language),
                          })}
                        </span>
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}
          </>
        )}

        {bounds && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <span className="font-medium text-sm">
              {t("slots.pickAnother.label")}
            </span>
            <DatePicker
              calendar={{
                disabled: [
                  { before: compactToLocalDate(bounds.min) },
                  { after: compactToLocalDate(bounds.max) },
                ],
                endMonth: compactToLocalDate(bounds.max),
                locale: language === "es" ? es : undefined,
                startMonth: compactToLocalDate(bounds.min),
              }}
              disabled={pickLoading || submitting}
              formatValue={(date) =>
                formatDateLabel(localDateToCompact(date), language)
              }
              onValueChange={handlePick}
              placeholder={t("slots.pickAnother.placeholder")}
              required={true}
              trigger={{ className: "sm:w-auto" }}
              value={pickedDate ? compactToLocalDate(pickedDate) : null}
            />
            {pickedDate !== null && (
              <button
                className="text-primary text-sm hover:underline"
                onClick={handleShowEarliest}
                type="button"
              >
                {t("slots.pickAnother.showEarliest")}
              </button>
            )}
          </div>
        )}

        {pickError !== null && (
          <p className="font-medium text-[0.8rem] text-destructive">
            {t(errorKey("slots.loadErrors", pickError))}
          </p>
        )}

        {needsContact && (
          <ContactCapture contact={contact} onChange={setContact} />
        )}

        {submitError !== null && (
          <p className="font-medium text-[0.8rem] text-destructive">
            {t(errorKey("slots.errors", submitError))}
          </p>
        )}

        <Button
          disabled={
            !(activeDay && selectedWindowId) ||
            submitting ||
            (needsContact && !isCompleteContact(contact))
          }
          onClick={handleConfirm}
        >
          {submitting ? t("slots.submitting") : t("slots.confirm")}
        </Button>
      </Card.Content>
    </Card>
  );
}
